// Plan configuration for Promptwizer.
// Single source of truth for tier names, limits, prices, and the Stripe IDs
// the client uses to start checkout. Edge Functions read the same shape via
// supabase/functions/_shared/plan-config.ts.
//
// SYNC CHECK: bump PLAN_CONFIG_SYNC_VERSION in BOTH files whenever you change
// any limit, price, preview cap, or share-link cap. Mismatched versions mean
// the two files have drifted.
export const PLAN_CONFIG_SYNC_VERSION = '2026-06-03';

export type PlanKey = 'free' | 'starter' | 'pro' | 'team' | 'max';
export type BillingInterval = 'monthly' | 'annual';

export const PLAN_KEYS: PlanKey[] = ['free', 'starter', 'pro', 'team', 'max'];

const env = (key: string, fallback = ''): string => {
  const value = import.meta.env[key] ?? fallback;
  return typeof value === 'string' && !value.includes('PLACEHOLDER') ? value : '';
};

// Stripe product/price IDs are read from Vite env vars. Checkout buttons only
// activate once both the product and price ID are set for the tier.
// Annual prices reuse the monthly product ID (standard Stripe pattern: one
// Product, multiple Prices), so no separate annual product IDs exist.
export const PLAN_CONFIG = {
  STARTER_PRODUCT_ID: env('VITE_STRIPE_STARTER_PRODUCT_ID'),
  PRO_PRODUCT_ID:     env('VITE_STRIPE_PRO_PRODUCT_ID'),
  TEAM_PRODUCT_ID:    env('VITE_STRIPE_TEAM_PRODUCT_ID'),
  MAX_PRODUCT_ID:     env('VITE_STRIPE_MAX_PRODUCT_ID'),
  API_ADDON_PRODUCT_ID: env('VITE_STRIPE_API_ADDON_PRODUCT_ID'),

  STARTER_PRICE_ID: env('VITE_STRIPE_STARTER_PRICE_ID'),
  PRO_PRICE_ID:     env('VITE_STRIPE_PRO_PRICE_ID'),
  TEAM_PRICE_ID:    env('VITE_STRIPE_TEAM_PRICE_ID'),
  MAX_PRICE_ID:     env('VITE_STRIPE_MAX_PRICE_ID'),
  API_ADDON_PRICE_ID: env('VITE_STRIPE_API_ADDON_PRICE_ID'),

  STARTER_ANNUAL_PRICE_ID: env('VITE_STRIPE_STARTER_ANNUAL_PRICE_ID'),
  PRO_ANNUAL_PRICE_ID:     env('VITE_STRIPE_PRO_ANNUAL_PRICE_ID'),
  TEAM_ANNUAL_PRICE_ID:    env('VITE_STRIPE_TEAM_ANNUAL_PRICE_ID'),
  MAX_ANNUAL_PRICE_ID:     env('VITE_STRIPE_MAX_ANNUAL_PRICE_ID'),

  // Build credits per billing period (1 build = 0.5 start + 0.5 finish).
  limits: {
    free:    2,     // per calendar month
    starter: 25,
    pro:     150,
    team:    500,
    max:     1500,
  } as const satisfies Record<PlanKey, number>,

  names: {
    free:    'Free',
    starter: 'Starter',
    pro:     'Pro',
    team:    'Team',
    max:     'Max',
  } as const satisfies Record<PlanKey, string>,

  prices: {
    free:    0,
    starter: 7.99,
    pro:     17.99,
    team:    89.99,
    max:     179.99,
  } as const satisfies Record<PlanKey, number>,

  // Annual = monthly × 12 × 0.85 (15% off), rounded up to a .99 ending.
  annualPrices: {
    free:    0,
    starter: 81.99,
    pro:     183.99,
    team:    917.99,
    max:     1835.99,
  } as const satisfies Record<PlanKey, number>,

  // Daily ceiling on preview-prompt runs. 'unlimited' means no row count check.
  previewDailyLimits: {
    free:    0,            // free plan does not get previews
    starter: 5,
    pro:     20,
    team:    'unlimited',
    max:     'unlimited',
  } as const satisfies Record<PlanKey, number | 'unlimited'>,

  // Active public share links per user. 'unlimited' = no cap.
  shareLinkLimits: {
    free:    1,
    starter: 'unlimited',
    pro:     'unlimited',
    team:    'unlimited',
    max:     'unlimited',
  } as const satisfies Record<PlanKey, number | 'unlimited'>,

  apiAddon: {
    priceUsd:        79.99,
    requestsPerDay:  200,
    name:            'API access',
  },
} as const;

const PRODUCT_TO_PLAN: Array<[string, PlanKey]> = ([
  [PLAN_CONFIG.STARTER_PRODUCT_ID, 'starter'],
  [PLAN_CONFIG.PRO_PRODUCT_ID,     'pro'],
  [PLAN_CONFIG.TEAM_PRODUCT_ID,    'team'],
  [PLAN_CONFIG.MAX_PRODUCT_ID,     'max'],
] satisfies Array<[string, PlanKey]>).filter(([productId]) => Boolean(productId));

export const getPriceIdForPlan = (
  plan: Exclude<PlanKey, 'free'>,
  interval: BillingInterval = 'monthly',
): string => {
  if (interval === 'annual') {
    if (plan === 'starter') return PLAN_CONFIG.STARTER_ANNUAL_PRICE_ID;
    if (plan === 'pro') return PLAN_CONFIG.PRO_ANNUAL_PRICE_ID;
    if (plan === 'team') return PLAN_CONFIG.TEAM_ANNUAL_PRICE_ID;
    return PLAN_CONFIG.MAX_ANNUAL_PRICE_ID;
  }
  if (plan === 'starter') return PLAN_CONFIG.STARTER_PRICE_ID;
  if (plan === 'pro') return PLAN_CONFIG.PRO_PRICE_ID;
  if (plan === 'team') return PLAN_CONFIG.TEAM_PRICE_ID;
  return PLAN_CONFIG.MAX_PRICE_ID;
};

export const getProductIdForPlan = (plan: Exclude<PlanKey, 'free'>): string => {
  if (plan === 'starter') return PLAN_CONFIG.STARTER_PRODUCT_ID;
  if (plan === 'pro') return PLAN_CONFIG.PRO_PRODUCT_ID;
  if (plan === 'team') return PLAN_CONFIG.TEAM_PRODUCT_ID;
  return PLAN_CONFIG.MAX_PRODUCT_ID;
};

export const getAnnualPriceForPlan = (plan: PlanKey): number =>
  PLAN_CONFIG.annualPrices[plan];

export const isPlanCheckoutConfigured = (
  plan: PlanKey,
  interval: BillingInterval = 'monthly',
): boolean => {
  if (plan === 'free') return true;
  return Boolean(getPriceIdForPlan(plan, interval) && getProductIdForPlan(plan));
};

export const isApiAddonCheckoutConfigured = (): boolean =>
  Boolean(PLAN_CONFIG.API_ADDON_PRICE_ID && PLAN_CONFIG.API_ADDON_PRODUCT_ID);

export const getPlanFromProductId = (productId: string | null): PlanKey => {
  if (!productId) return 'free';
  return PRODUCT_TO_PLAN.find(([id]) => id === productId)?.[1] ?? 'free';
};

export const getBuildLimit = (plan: PlanKey): number =>
  PLAN_CONFIG.limits[plan];

export const getPreviewDailyLimit = (plan: PlanKey): number | 'unlimited' =>
  PLAN_CONFIG.previewDailyLimits[plan];

export const getShareLinkLimit = (plan: PlanKey): number | 'unlimited' =>
  PLAN_CONFIG.shareLinkLimits[plan];

// All Stripe price IDs the create-checkout edge function should accept.
export const ALL_PRICE_IDS: readonly string[] = [
  isPlanCheckoutConfigured('starter', 'monthly') ? PLAN_CONFIG.STARTER_PRICE_ID : '',
  isPlanCheckoutConfigured('pro', 'monthly') ? PLAN_CONFIG.PRO_PRICE_ID : '',
  isPlanCheckoutConfigured('team', 'monthly') ? PLAN_CONFIG.TEAM_PRICE_ID : '',
  isPlanCheckoutConfigured('max', 'monthly') ? PLAN_CONFIG.MAX_PRICE_ID : '',
  isPlanCheckoutConfigured('starter', 'annual') ? PLAN_CONFIG.STARTER_ANNUAL_PRICE_ID : '',
  isPlanCheckoutConfigured('pro', 'annual') ? PLAN_CONFIG.PRO_ANNUAL_PRICE_ID : '',
  isPlanCheckoutConfigured('team', 'annual') ? PLAN_CONFIG.TEAM_ANNUAL_PRICE_ID : '',
  isPlanCheckoutConfigured('max', 'annual') ? PLAN_CONFIG.MAX_ANNUAL_PRICE_ID : '',
  isApiAddonCheckoutConfigured() ? PLAN_CONFIG.API_ADDON_PRICE_ID : '',
].filter(Boolean);
